import React from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import CheckInHeader from '../../components/check-in/CheckInHeader';
import CheckInNavBar from '../../components/check-in/CheckInNavBar';
import { getCategoryData, type SubEmotion } from '../../data/check-in';
import { containerVariants, itemVariants } from '../../lib/animations';
import { useAnalytics } from '../../contexts/AnalyticsContext';

export default function CheckInSubEmotionsPage() {
  const { track } = useAnalytics();
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();

  const data = category ? getCategoryData(category) : undefined;

  // Invalid category — redirect to weather picker
  if (!data) {
    return <Navigate to="/check-in/weather" replace />;
  }

  const handleSubEmotionSelect = (subEmotionName: string) => {
    track('sub_emotion_select', { category: category ?? '', sub_emotion: subEmotionName.toLowerCase() });
    navigate(
      `/check-in/${category}/strategies?sub=${encodeURIComponent(subEmotionName.toLowerCase())}`
    );
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(to bottom, #58C5DA 25%, #C7EDEF 85%)' }}
    >
      <div className="relative z-10 flex flex-col">
        <CheckInHeader />
        <CheckInNavBar
          label="BACK TO STATE SELECTION"
          to="/check-in/weather"
        />

        <motion.div
          className="flex flex-col items-center px-4 mt-8 w-full max-w-md lg:max-w-xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Category title */}
          <motion.h2
            className="text-[#1B2831] font-bold text-[32px] md:text-[34px] lg:text-[36px] text-center"
            style={{ fontFamily: 'DM Sans', letterSpacing: '-1.28px' }}
            variants={itemVariants}
          >
            {data.name}
          </motion.h2>

          {/* Category subtitle */}
          <motion.p
            className="text-base text-[#1B2831] text-center max-w-[382px] mt-1"
            style={{ fontFamily: 'DM Sans' }}
            variants={itemVariants}
          >
            {data.subtitle}
          </motion.p>

          {/* Sub-emotion clouds — circular layout: center + 6 around */}
          <motion.div
            className="mt-16 relative w-full max-w-[382px] md:max-w-[440px] lg:max-w-[480px] aspect-square"
            variants={itemVariants}
          >
            {/* Center cloud */}
            <SubEmotionCloud
              subEmotion={data.subEmotions[0]}
              index={0}
              onSelect={handleSubEmotionSelect}
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
            {/* 6 surrounding clouds at equal 60° intervals */}
            {data.subEmotions.slice(1).map((sub, i) => {
              const angle = (i * 60 - 90) * (Math.PI / 180);
              const radius = 120;
              const x = 50 + (radius / 340) * 100 * Math.cos(angle);
              const y = 50 + (radius / 340) * 100 * Math.sin(angle);
              return (
                <SubEmotionCloud
                  key={sub.name}
                  subEmotion={sub}
                  index={i + 1}
                  onSelect={handleSubEmotionSelect}
                  style={{
                    position: 'absolute',
                    left: `${x}%`,
                    top: `${y}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              );
            })}
          </motion.div>

          {/* Tip text */}
          <motion.div className="w-full max-w-[382px] mt-16 mb-8" variants={itemVariants}>
            <p
              className="text-base text-[#1B2831] text-center"
              style={{ fontFamily: 'DM Sans' }}
            >
              TIP: {data.tip}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

interface SubEmotionCloudProps {
  subEmotion: SubEmotion;
  index: number;
  onSelect: (name: string) => void;
  style?: React.CSSProperties;
}

function SubEmotionCloud({ subEmotion, index, onSelect, style }: SubEmotionCloudProps) {
  return (
    <button
      onClick={() => onSelect(subEmotion.name)}
      className="flex flex-col items-center gap-1 p-2 w-[32%] hover:scale-105 transition-transform"
      style={{
        ...style,
        opacity: 0,
        animation: `fadeIn 0.4s ease-out ${index * 0.1}s forwards`,
      }}
    >
      <img
        src={subEmotion.asset}
        alt={subEmotion.name}
        className="w-full"
      />
      <span
        className="text-xs font-black uppercase text-[#1B2831]"
        style={{ fontFamily: 'DM Sans', letterSpacing: '0.72px' }}
      >
        {subEmotion.label}
      </span>
    </button>
  );
}
